/**
 * Dashboard Page
 * დეშბორდის გვერდი
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Spinner } from '@aiia/portal-core';
import { useApiKeys } from '../hooks/useApiKeys';
import { useDataFiles } from '../hooks/useDataFiles';
import { useAnalyticsOverview, useAnalyticsTraffic } from '../hooks/useAnalytics';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import TrendLineChart from '../components/admin/statistics/TrendLineChart';
import ChannelBarChart from '../components/admin/statistics/ChannelBarChart';
import PeriodSelector from '../components/admin/statistics/PeriodSelector';
import type { StatisticsPeriod } from '../features/api/analytics';

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  color,
  loading
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
  loading?: boolean;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {loading ? (
            <Spinner size="sm" className="mt-2" />
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
              )}
            </>
          )}
        </div>
        <div className={`p-3 rounded-full ${color || 'bg-blue-100 text-blue-600'}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const [period, setPeriod] = useState<StatisticsPeriod>('MONTH');
  const { apiKeys } = useApiKeys();
  const { dataFiles } = useDataFiles();
  const { overview, isLoading: loadingOverview } = useAnalyticsOverview();
  const { traffic, isLoading: loadingTraffic } = useAnalyticsTraffic(period);

  const activeApiKeys = apiKeys?.filter(k => k.status === 'ACTIVE').length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-600 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Analytics Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('analytics.conversations')}
          value={formatNumber(overview?.totalConversations || 0)}
          subtitle={overview?.conversationsToday ? `+${overview.conversationsToday} ${t('analytics.today')}` : undefined}
          loading={loadingOverview}
          color="bg-indigo-100 text-indigo-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.messages')}
          value={formatNumber(overview?.totalMessages || 0)}
          subtitle={overview?.messagesToday ? `+${overview.messagesToday} ${t('analytics.today')}` : undefined}
          loading={loadingOverview}
          color="bg-violet-100 text-violet-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.tokens')}
          value={formatNumber(overview?.totalTokens || 0)}
          loading={loadingOverview}
          color="bg-amber-100 text-amber-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <StatCard
          title={t('analytics.widgets')}
          value={`${overview?.activeWidgets || 0} / ${overview?.totalWidgets || 0}`}
          subtitle={`${activeApiKeys} ${t('analytics.apiKeys')}, ${dataFiles?.length || 0} ${t('analytics.dataFiles')}`}
          loading={loadingOverview}
          color="bg-emerald-100 text-emerald-600"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
        />
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.conversationTrend || []}
              title={t('analytics.conversationTrend')}
              color="#6366f1"
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <ChannelBarChart
              data={traffic?.conversationsByChannel || {}}
              title={t('analytics.byChannel')}
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.messageTrend || []}
              title={t('analytics.messageTrend')}
              color="#8b5cf6"
            />
          )}
        </Card>

        <Card className="p-6">
          {loadingTraffic ? (
            <div className="flex items-center justify-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <TrendLineChart
              data={traffic?.tokenTrend || []}
              title={t('analytics.tokenTrend')}
              color="#f59e0b"
            />
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <a href={lp('/api-keys')} className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <p className="font-medium text-gray-900">{t('dashboard.createApiKey')}</p>
            <p className="text-sm text-gray-500">{t('dashboard.createApiKeyDesc')}</p>
          </a>
          <a href={lp('/data-files')} className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <p className="font-medium text-gray-900">{t('dashboard.uploadDataFile')}</p>
            <p className="text-sm text-gray-500">{t('dashboard.uploadDataFileDesc')}</p>
          </a>
          <a href={lp('/widgets')} className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <p className="font-medium text-gray-900">{t('dashboard.customizeWidget')}</p>
            <p className="text-sm text-gray-500">{t('dashboard.customizeWidgetDesc')}</p>
          </a>
        </div>
      </Card>
    </div>
  );
}
